import React, { forwardRef } from "react";

import { cn } from "src/shadcn/lib/utils";

import { isValidHex } from "./utils";
import { DEFAULT_PALETTE_COLORS } from "./constants";
import type { PaletteColor } from "./types";

export interface PaletteProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  /** Currently selected color. */
  color?: string;
  /** List of colors to render. */
  colorList?: PaletteColor[];
  /** Callback when a color swatch is clicked. */
  onChange?: (color: string) => void;
}

const Palette = forwardRef<HTMLDivElement, PaletteProps>(
  (
    {
      color,
      colorList = DEFAULT_PALETTE_COLORS,
      onChange,
      className,
      ...otherProps
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-row flex-wrap items-start justify-start gap-1",
        className
      )}
      {...otherProps}
    >
      {colorList.map(item => {
        const rgbValue =
          "rgb" in item && typeof item.rgb === "string" ? item.rgb : undefined;
        const value = item.hex ?? rgbValue ?? "";
        const isActive =
          !!color && !!value && color.toLowerCase() === value.toLowerCase();

        return (
          <button
            type="button"
            key={value}
            data-testid="color-palette-item"
            className={cn(
              "size-6 rounded-[5px] border cursor-pointer overflow-hidden transition-colors",
              isActive
                ? "border-muted-foreground"
                : "border-border hover:border-muted-foreground/50"
            )}
            onClick={() => onChange?.(value)}
          >
            <span
              className={cn(
                "block size-full",
                isValidHex(value)
                  ? ""
                  : "bg-[repeating-conic-gradient(#ccc_0_25%,transparent_0_50%)] bg-[length:8px_8px]"
              )}
              style={isValidHex(value) ? { backgroundColor: value } : undefined}
            />
          </button>
        );
      })}
    </div>
  )
);

Palette.displayName = "ColorPicker.Palette";

export { Palette };
